import type { PlatformType } from '@/types'

import type {
    NativePaymentMessage,
    NativePaymentOrder,
    NativePaymentPack,
    NativePaymentPlatform,
    NativePaymentSku,
    NativePurchaseCapabilities,
    NativePurchaseProduct,
    NativePurchaseResult,
    NativePurchaseStatus,
    NativePurchaseTransaction,
    NativePurchaseTransactionPayload,
} from './types'

export function getNativePaymentPlatform(platform: PlatformType): NativePaymentPlatform | undefined {
    return platform === 'ios' || platform === 'android' ? platform : undefined
}

export function getNativeProductCurrency(product: NativePurchaseProduct) {
    return product.currency || product.currencyCode || product.priceCurrencyCode
}

export function getNativeProductPriceAmount(product: NativePurchaseProduct) {
    if (typeof product.priceAmount === 'number') {
        return product.priceAmount
    }

    if (typeof product.price === 'number') {
        return product.price
    }

    const micros = product.priceAmountMicros ?? product.amountMicros

    if (typeof micros === 'number') {
        return micros / 1_000_000
    }

    return undefined
}

export function getNativeProductPriceLabel(product: NativePurchaseProduct) {
    if (product.localizedPrice || product.displayPrice) {
        return product.localizedPrice || product.displayPrice || ''
    }

    const currency = getNativeProductCurrency(product)

    if (typeof product.price === 'string') {
        const priceNumber = Number(product.price)

        if (Number.isFinite(priceNumber) && currency) {
            return formatNativeProductPrice(priceNumber, currency) || product.price
        }

        return product.price
    }

    const amount = getNativeProductPriceAmount(product)

    if (typeof amount === 'number' && currency) {
        return formatNativeProductPrice(amount, currency) || amount.toFixed(2)
    }

    if (typeof amount === 'number') {
        return amount.toFixed(2)
    }

    return product.sku
}

export function compareNativeProductPrice(a: NativePaymentPack, b: NativePaymentPack) {
    return (
        (getNativeProductPriceAmount(a.product) ?? Number.MAX_SAFE_INTEGER) -
        (getNativeProductPriceAmount(b.product) ?? Number.MAX_SAFE_INTEGER)
    )
}

export function getNativeProductsBySku(products: NativePurchaseProduct[]) {
    return Object.fromEntries(products.map((product) => [product.sku, product]))
}

export function selectLowestCoveringNativePaymentPack({
    amount,
    platform,
    products,
    skus,
}: {
    amount: number
    platform: NativePaymentPlatform
    products: Record<string, NativePurchaseProduct>
    skus: NativePaymentSku[]
}) {
    return skus
        .flatMap((sku) => {
            const product = products[sku.sku]

            if (
                sku.platform !== platform ||
                !product ||
                product.available === false ||
                sku.playgamaCurrencyAmount < amount
            ) {
                return []
            }

            return [{ ...sku, product }]
        })
        .sort((a, b) => a.playgamaCurrencyAmount - b.playgamaCurrencyAmount || compareNativeProductPrice(a, b))[0]
}

export function selectExactNativePaymentPack({
    amount,
    platform,
    products,
    skus,
}: {
    amount: number
    platform: NativePaymentPlatform
    products: Record<string, NativePurchaseProduct>
    skus: NativePaymentSku[]
}) {
    return skus
        .flatMap((sku) => {
            const product = products[sku.sku]

            if (
                sku.platform !== platform ||
                !product ||
                product.available === false ||
                sku.playgamaCurrencyAmount !== amount
            ) {
                return []
            }

            return [{ ...sku, product }]
        })
        .sort((a, b) => compareNativeProductPrice(a, b))[0]
}

export function isNativePurchaseCapabilities(
    payload: NativePaymentMessage['payload'],
): payload is NativePurchaseCapabilities {
    if (!payload) {
        return false
    }

    return (
        'available' in payload &&
        'canMakePayments' in payload &&
        typeof payload.available === 'boolean' &&
        typeof payload.canMakePayments === 'boolean'
    )
}

export function isNativePurchaseProductsPayload(
    payload: NativePaymentMessage['payload'],
): payload is { products: NativePurchaseProduct[] } {
    return payload ? 'products' in payload && Array.isArray(payload.products) : false
}

export function isNativePurchaseUnfinishedPayload(
    payload: NativePaymentMessage['payload'],
): payload is { transactions: NativePurchaseTransactionPayload[] } {
    return payload ? 'transactions' in payload && Array.isArray(payload.transactions) : false
}

export function getNativePurchaseStatus(payload: NativePaymentMessage['payload'] | NativePurchaseResult | undefined) {
    if (!payload || !('status' in payload) || typeof payload.status !== 'string') {
        return undefined
    }

    return payload.status.toLowerCase() as NativePurchaseStatus
}

export function getNativePurchaseResult(payload: NativePaymentMessage['payload']) {
    if (!payload || typeof payload !== 'object') {
        return undefined
    }

    return payload as NativePurchaseResult
}

export function isSuccessfulNativePurchaseStatus(status: NativePurchaseStatus | undefined) {
    return status === 'purchased'
}

export function getNativePurchaseMessage(payload: NativePurchaseResult | undefined) {
    return payload?.message || payload?.error || payload?.description
}

export function normalizeNativePurchaseTransaction(
    payload: NativePurchaseTransactionPayload | undefined,
): NativePurchaseTransaction | undefined {
    const receipt = payload && getNativePurchaseReceipt(payload)

    if (!payload?.sku || !payload.transactionId || !receipt) {
        return undefined
    }

    return {
        sku: payload.sku,
        transactionId: payload.transactionId,
        receipt,
    }
}

export function getNativePaymentSource(order: NativePaymentOrder) {
    if (order?.type === 'disable-ad-order') {
        return 'adFree'
    }

    if (order?.type === 'order') {
        return 'game'
    }

    return 'shop'
}

export function getNativeVerificationErrorMessage(response: Response, bodyText: string | undefined, errors: string[]) {
    const status = [response.status, response.statusText].filter(Boolean).join(' ')
    const responseBody = bodyText?.trim()

    if (responseBody) {
        return `Native purchase verification failed (${status}): ${responseBody}`
    }

    return errors[0] || `Native purchase verification failed (${status})`
}

function getNativePurchaseReceipt(payload: NativePurchaseTransactionPayload) {
    return payload.receipt || payload.purchaseToken || payload.token
}

function formatNativeProductPrice(amount: number, currency: string) {
    try {
        return new Intl.NumberFormat(undefined, {
            style: 'currency',
            currency: currency.toUpperCase(),
        }).format(amount)
    } catch (error) {
        console.error('Failed to format native product price', error)
        return undefined
    }
}
